import React, { useEffect, useRef } from 'react';

const InteractiveTechBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let width, height;
        let particles = [];

        // Configuration
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const particleCount = window.innerWidth < 768 ? 36 : 64;
        const connectionDistance = 150;
        const mouseDistance = 250;

        // Resize handler
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            width = rect.width;
            height = rect.height;

            // Set internal resolution to match display size
            canvas.width = width;
            canvas.height = height;
        };

        // Mouse tracking
        let mouse = { x: null, y: null };

        // Helper to get relative coordinates
        const getRelativeCoords = (e) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        };

        // Particle Class
        class Particle {
            constructor() {
                this.x = Math.random() * width;
                this.y = Math.random() * height;
                this.vx = (Math.random() - 0.5) * 0.5;
                this.vy = (Math.random() - 0.5) * 0.5;
                this.size = Math.random() * 2 + 1;
                // Randomly assign white/grey or red for dark mode
                this.color = Math.random() > 0.5 ? '#e5e7eb' : '#dc2626'; // gray-200 or red-600
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;

                // Bounce off edges
                if (this.x < 0 || this.x > width) this.vx *= -1;
                if (this.y < 0 || this.y > height) this.vy *= -1;

                // Mouse interaction
                if (mouse.x != null) {
                    let dx = mouse.x - this.x;
                    let dy = mouse.y - this.y;
                    let distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance > 0 && distance < mouseDistance) {
                        const forceDirectionX = dx / distance;
                        const forceDirectionY = dy / distance;
                        const force = (mouseDistance - distance) / mouseDistance;

                        // Stronger Repulsion
                        const direction = -2.5;

                        this.vx += forceDirectionX * force * direction * 0.1;
                        this.vy += forceDirectionY * force * direction * 0.1;
                    }
                }
            }

            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = this.color;
                ctx.fill();
            }
        }

        // Initialize particles
        const init = () => {
            resize();
            particles = [];
            for (let i = 0; i < particleCount; i++) {
                particles.push(new Particle());
            }
        };

        // Animation loop
        const animate = () => {
            ctx.clearRect(0, 0, width, height);

            for (let i = 0; i < particles.length; i++) {
                particles[i].update();
                particles[i].draw();

                // Connect to Mouse
                if (mouse.x != null) {
                    let dx = mouse.x - particles[i].x;
                    let dy = mouse.y - particles[i].y;
                    let distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < 200) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(220, 38, 38, ${1 - distance / 200})`; // Red connection to mouse
                        ctx.lineWidth = 1.5;
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(mouse.x, mouse.y);
                        ctx.stroke();
                    }
                }

                // Draw connections
                for (let j = i; j < particles.length; j++) {
                    let dx = particles[i].x - particles[j].x;
                    let dy = particles[i].y - particles[j].y;
                    let distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < connectionDistance) {
                        ctx.beginPath();
                        let strokeColor;
                        if (particles[i].color === '#dc2626' || particles[j].color === '#dc2626') {
                            // Red glow connection
                            strokeColor = `rgba(220, 38, 38, ${1 - distance / connectionDistance})`;
                        } else {
                            // White/Grey connection for dark mode
                            strokeColor = `rgba(255, 255, 255, ${(1 - distance / connectionDistance) * 0.2})`;
                        }

                        ctx.strokeStyle = strokeColor;
                        ctx.lineWidth = 1;
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.stroke();
                    }
                }
            }
            if (!prefersReducedMotion) {
                animationFrameId = requestAnimationFrame(animate);
            }
        };

        // Mouse interaction
        const handleMouseMove = (e) => {
            const coords = getRelativeCoords(e);
            mouse.x = coords.x;
            mouse.y = coords.y;
        };
        const handleMouseOut = () => {
            mouse.x = null;
            mouse.y = null;
        };
        const handleTouchEnd = () => {
            mouse.x = null;
            mouse.y = null;
        };

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseout', handleMouseOut);

        // Touch interaction (Mobile)
        const handleTouch = (e) => {
            if (e.touches.length > 0) {
                const touch = e.touches[0];
                const coords = getRelativeCoords(touch);
                mouse.x = coords.x;
                mouse.y = coords.y;
            }
        };

        window.addEventListener('touchstart', handleTouch, { passive: true });
        window.addEventListener('touchmove', handleTouch, { passive: true });
        window.addEventListener('touchend', handleTouchEnd);

        init();
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseout', handleMouseOut);
            window.removeEventListener('touchstart', handleTouch);
            window.removeEventListener('touchmove', handleTouch);
            window.removeEventListener('touchend', handleTouchEnd);
            if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                background: 'transparent' // Transparent to show CSS gradient
            }}
        />
    );
};

export default InteractiveTechBackground;
